import moment from 'moment';
import { result } from 'lodash/object';
import { titleCase } from 'change-case';

import { generateShipmentPDFReceipt } from 'utils/receipt_pdf_generator';
import { trimMoney, truncate } from 'utils/strings';

import { ACL_SHIPMENT_CREATION } from 'app/routers/account_router';

import Shipments from 'app/collections/shipments';
import DropdownSelections from 'app/collections/dropdown_selections';
import ApiModeSelection from 'app/models/api_mode_selection';
import DateRange, { DATERANGE_PARAM_TYPES } from 'app/models/date_range';
import ShipmentReport from 'app/models/shipment_report';

import Base from 'app/views/base';
import DatepickerRangeView from 'app/views/datepicker_range_view';
import ShipmentRefundModal from 'app/views/modals/shipment_refund_modal';
import SelectorView from 'app/views/selector_view';

import { List as ListView, Heading } from 'app/components/list/list';
import TrackingCodeHeadingView from 'app/components/list/item/headings/tracking_code';
import DateAndTimeHeadingView from 'app/components/list/item/headings/date_and_time';
import { DetailBlock } from 'app/components/list/object_details';
import ListSearchView from 'app/components/list/search';
import PageHeaderView from 'app/components/page_header';
import RequestReportView from 'app/components/request_report';
import ShipmentCreateView from 'app/components/pages/shipments/create';
import ShipmentDetailModal from 'app/components/pages/shipments/detail_modal';

import tmpl from './template';
import parcelDetailTemplate from './parcel_detail';
import postageLabelButtonTemplate from './postage_label_button';
import receiptButtonTemplate from './receipt_button';
import refundButtonTemplate from './refund_button';
import copyButtonTemplate from './copy_button';
import trackButtonTemplate from './track_button';

export const ParcelDetailView = Base.extend({
  compiledTemplate: parcelDetailTemplate,

  context() {
    const parcel = this.model.get('parcel');
    return {
      dimensions: result(parcel, 'dimensions'),
      predefinedPackage: parcel ? parcel.get('predefined_package') : '',
      weight: result(parcel, 'weight'),
    };
  },
});

export const PostageLabelButtonView = Base.extend({
  compiledTemplate: postageLabelButtonTemplate,

  context() {
    return { postageLabelUrl: result(this.model.get('postage_label'), 'imageUrl') };
  },
});

export const ReceiptButtonView = Base.extend({
  compiledTemplate: receiptButtonTemplate,

  initialize({ user }) {
    this.user = user;
  },

  events: { 'click .receipt': 'generateReceipt' },

  context() {
    const fees = this.model.get('fees');
    return { hasFees: Array.isArray(fees) && !!fees.length };
  },

  generateReceipt(event) {
    event.preventDefault();
    const createdDate = moment(this.model.get('created_at') || '').utc();
    const fees = this.model.get('fees');

    if (!createdDate.isValid()) throw new Error('created_at property not present');
    if (!fees.length) throw new Error('no fees on this shipment');

    const filename = `easypost_${createdDate.format('YYYYMMDDHHmmss')}_${this.model.id}`;

    generateShipmentPDFReceipt(filename, {
      account: this.user.get('email'),
      name: this.user.get('name'),
      date: createdDate.format('YYYY-MM-DD'),
      time: createdDate.format('HH:mm:ss'),
      timezone: 'UTC',
      id: this.model.id,
      fees,
    });
  },
});

export const CopyButtonView = Base.extend({
  compiledTemplate: copyButtonTemplate,

  events: { 'click button': 'copy' },

  initialize({ onClick = () => {} }) {
    this.onClick = onClick;
  },

  copy() {
    this.onClick(this.model);
  },
});

export const TrackButtonView = Base.extend({
  compiledTemplate: trackButtonTemplate,
  context() {
    return this.model.toJSON().tracker;
  },
});

export const RefundButtonView = Base.extend({
  compiledTemplate: refundButtonTemplate,

  events: { 'click .refund': 'showRefundModal' },

  initialize() {
    this.refundModal = new ShipmentRefundModal({ model: this.model });
  },

  context() {
    return {
      refundStatus: titleCase(this.model.get('refund_status')),
      purchased: !!this.model.get('tracking_code'),
    };
  },

  showRefundModal(event) {
    event.preventDefault();
    this.refundModal.show();
  },

});

export const buildHeadings = () => [
  new Heading({
    subView: m => new DateAndTimeHeadingView({ model: m }),
    display: 'Date',
    xsSize: 3,
    smSize: 2,
    wrapperClass: 'text-nowrap',
  }),
  new Heading({
    text: m => truncate(result(m.get('to_address'), 'nameAndCompany'), 25),
    display: 'To',
    xsSize: 5,
    smSize: 3,
    wrapperClass: 'text-truncate',
  }),
  new Heading({
    subView: m => new TrackingCodeHeadingView({ model: m.get('tracker') }),
    display: 'Tracking Code',
    xsSize: 4,
    smSize: 4,
  }),
  new Heading({
    text: m => {
      const currencyCode = result(m.get('selected_rate'), 'currency');
      const rate = result(m.get('selected_rate'), 'rate');
      return rate ? trimMoney(rate, currencyCode) : '';
    },
    display: 'Cost',
    smSize: 1,
    xsHidden: true,
  }),
];

export const buildShowDetails = (user, copy) => {
  const userACL = user.get('acl') || {};

  const buttonSubviews = [
    m => new TrackButtonView({ model: m.get('tracker') }),
    m => new PostageLabelButtonView({ model: m }),
    m => new ReceiptButtonView({ model: m, user }),
  ];

  if (userACL && userACL[ACL_SHIPMENT_CREATION]) {
    buttonSubviews.push(m => new CopyButtonView({ model: m, onClick: copy }));
  }

  buttonSubviews.push(m => new RefundButtonView({ model: m }));

  return {
    mainHeading: m => m.get('id'),
    detailBlocks: [
      new DetailBlock({
        heading: 'From',
        items: m => (m.get('from_address') ? result(m.get('from_address'), 'addressArray') : []),
      }),
      new DetailBlock({
        heading: 'To',
        items: m => (m.get('to_address') ? result(m.get('to_address'), 'addressArray') : []),
      }),
      new DetailBlock({
        heading: 'Parcel',
        subViews: [m => new ParcelDetailView({ model: m })],
      }),
      new DetailBlock({
        heading: '',
        wrapperSubClass: 'buttons',
        subViews: buttonSubviews,
      }),
    ],
  };
};

export default Base.extend({
  compiledTemplate: tmpl,
  className: 'shipments',

  initialize({ user, query = {} }) {
    this.user = user;
    this.apiModeSelection = new ApiModeSelection({});
    this.dateRange = new DateRange({ paramType: DATERANGE_PARAM_TYPES.DATETIME });
    this.copy = this.copy.bind(this);

    this.dropdownSelections = new DropdownSelections(
      [this.apiModeSelection, this.dateRange],
      { initialChoices: query[this.className], urlParamKey: this.className },
    );

    this.collection = new Shipments([], { dropdownSelections: this.dropdownSelections });

    this.createView = new ShipmentCreateView({
      apiModeSelection: this.apiModeSelection,
    });

    this.listenTo(this.collection, 'update', this.render);

    const headerSubviews = [];

    const userACL = user.get('acl') || {};

    if (userACL && userACL[ACL_SHIPMENT_CREATION]) {
      headerSubviews.push(this.createView);
    }

    this.addSubview('header', 'header', new PageHeaderView({
      name: 'Shipments',
      links: [
        { name: 'Docs', href: '/docs/api#shipments' },
        { name: 'Guide', href: '/getting-started' },
      ],
      subViews: headerSubviews,
    }));

    this.addSubview('apiModeSelector', '#api_mode_selector', new SelectorView({ model: this.apiModeSelection }));
    this.addSubview('datepickers', '#shipment_daterange_pickers', new DatepickerRangeView({ model: this.dateRange }));
    this.addSubview('requestShipmentReport', '#request_shipment_report', new RequestReportView({
      ReportClass: ShipmentReport,
      dateRange: this.dateRange,
      apiModeSelection: this.apiModeSelection,
    }));

    this.searchView = new ListSearchView({
      collection: this.collection,
      apiModeSelection: this.apiModeSelection,
      placeholder: '(Tracking Code, ID, or Reference)',
    });

    this.addSubview('search', '#shipment_search', this.searchView);

    this.addSubview('shipmentList', '#shipment_list', new ListView({
      headings: buildHeadings(),
      collection: this.collection,
      noResultsMessage: 'No postage has been purchased matching your filter set.',
      showDetails: buildShowDetails(user, this.copy),
    }));

    this.createView.on('buy:success', shipment => {
      window.open(shipment.get('postage_label').get('label_url'), 'EasyPost Shipment Label');

      this.detailModal = new ShipmentDetailModal({
        model: shipment,
        title: `Success! Tracking: ${shipment.get('tracking_code')}`,
        showDetails: buildShowDetails(user, this.copy),
      });

      this.createView.once('closed', () => {
        this.detailModal.show();
        this.searchView.clearSearch();
        this.searchView.search();
      });

      this.createView.closeModal();
    });
  },

  copy(shipment) {
    const newShipment = shipment.clone();
    newShipment.set({
      rates: [],
    });

    this.createView.resetModal(newShipment);

    if (this.detailModal) {
      this.detailModal.once('closed', () => {
        this.createView.showModal();
      });

      this.detailModal.close();
    } else {
      this.createView.showModal();
    }
  },
});



// WEBPACK FOOTER //
// ./app/components/pages/shipments/index.jsx